import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React from 'react';

import AllImagesButton from '@/app/[locale]/(default-layout)/info/_components/AllImagesButton';
import PreviewImage from '@/app/[locale]/(default-layout)/info/_components/PreviewImage';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

type PreviewImagesProps = {
    domainConfiguration: DomainConfigurationDto;
    className?: string;
    cinema: CinemaDto;
    images: string[];
    /** Total number of images across the whole gallery (building + all cinema rooms), shown on the "all images" button. */
    totalImagesCount?: number;
    onImageClick?: (index: number) => void;
    onShowAllImagesButtonClick?: () => void;
};

export default function PreviewImages(props: PreviewImagesProps): React.JSX.Element | null {
    const { domainConfiguration, className, onImageClick, onShowAllImagesButtonClick } = props;
    const t = useTranslations();
    const images = props.images?.filter((it) => !!it)?.slice(0, 4) || [];
    if (images.length <= 0) {
        return null;
    }
    const mobileView = (
        <div className="w-full h-full flex md:hidden flex-col space-y-4">
            {images.slice(0, 3).map((image, index) => (
                <PreviewImage
                    key={`PreviewImage#${index}`}
                    className="h-1/3 bg-[var(--surface-color)]"
                    alt={t('info.cinema-preview-image-alt', { index: index + 1, cinemaName: props.cinema.name })}
                    src={image}
                    onClick={onImageClick ? () => onImageClick(index) : undefined}
                />
            ))}
        </div>
    );

    const desktopView = (
        <div
            className={classnames(
                'w-full h-full hidden md:grid gap-12',
                images.length >= 3
                    ? 'grid-cols-2 grid-rows-2'
                    : images.length >= 2
                      ? 'grid-cols-1 grid-rows-2'
                      : 'grid-rows-1'
            )}>
            {images.map((image, index) => (
                <PreviewImage
                    key={`PreviewImage#${index}`}
                    className={classnames('!h-auto bg-[var(--surface-color)]', {
                        ['col-span-2']: index === 2 && images.length < 4
                    })}
                    alt={t('info.cinema-preview-image-alt', { index: index + 1, cinemaName: props.cinema.name })}
                    src={image}
                    onClick={onImageClick ? () => onImageClick(index) : undefined}
                />
            ))}
        </div>
    );

    return (
        <div className={classnames(className, 'relative h-full flex flex-col space-y-16')}>
            <div className="w-[33vw] md:w-[25vw] h-full flex-none">
                {mobileView}
                {desktopView}
                {onShowAllImagesButtonClick && (props.totalImagesCount ?? 0) > 0 && (
                    <AllImagesButton
                        domainConfiguration={domainConfiguration}
                        count={props.totalImagesCount ?? 0}
                        className="absolute right-6 bottom-6"
                        onClick={() => onShowAllImagesButtonClick && onShowAllImagesButtonClick()}
                    />
                )}
            </div>
        </div>
    );
}
